import * as React from 'react';
import { API_BASE_URL, SiteConfig } from '../../config/bootstrap';
import branding from '../../generated/branding.json';

const STORAGE_KEY = 'quickcheck.siteConfig';

interface SiteConfigContextValue {
  config: SiteConfig | null;
  loading: boolean;
  unavailable: boolean;
}

const SiteConfigContext = React.createContext<SiteConfigContextValue>({
  config: null,
  loading: true,
  unavailable: false,
});

const readCached = (): SiteConfig | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (parsed && parsed.siteId && Array.isArray(parsed.checks)) {
      return parsed as SiteConfig;
    }
  } catch {
    return null;
  }
  return null;
};

const writeCached = (config: SiteConfig) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    /* ignore quota */
  }
};

const clearCached = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

export const SiteConfigProvider: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
  const cached = typeof window !== 'undefined' ? readCached() : null;
  const [config, setConfig] = React.useState<SiteConfig | null>(cached);
  const [loading, setLoading] = React.useState(true);
  const [unavailable, setUnavailable] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/config`, { method: 'GET' });
        if (!response.ok) {
          throw new Error(`config ${response.status}`);
        }
        const data = (await response.json()) as SiteConfig;
        if (!data?.siteId) {
          throw new Error('invalid config');
        }
        if (!cancelled) {
          setConfig(data);
          setUnavailable(!(data.checks && data.checks.length > 0));
          writeCached(data);
        }
      } catch {
        if (!cancelled) {
          setConfig(null);
          setUnavailable(true);
          clearCached();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = React.useMemo(
    () => ({ config, loading, unavailable }),
    [config, loading, unavailable]
  );

  return <SiteConfigContext.Provider value={value}>{children}</SiteConfigContext.Provider>;
};

export const useSiteConfigState = (): SiteConfigContextValue => React.useContext(SiteConfigContext);

/** Runtime site config, or null when /config is unavailable. */
export const useSiteConfig = (): SiteConfig | null => React.useContext(SiteConfigContext).config;

/** Build-time branding used when runtime config is missing (masthead title only). */
export const buildTimeBranding = branding as {
  siteId: string;
  displayName: string;
  pageTitle: string;
  applicationName: string;
  themeClass: string;
  logoFile: string;
  faviconFile: string;
  portalClientId: string;
};
